import { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import axios from 'axios';
import {
    Grid3X3,
    Home,
    BarChart3,
    FolderKanban,
    Users,
    UserCog,
    Settings,
    LogOut,
    Menu,
    X,
    ChevronLeft,
    ChevronRight,
    Pencil,
    GripVertical,
    Check,
} from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
    DropdownMenu, 
    DropdownMenuContent, 
    DropdownMenuItem, 
    DropdownMenuSeparator, 
    DropdownMenuTrigger 
} from './ui/dropdown-menu';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from './ui/tooltip';
import { Avatar, AvatarFallback } from './ui/avatar';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const APP_ICONS = {
    analytics: BarChart3,
    projects: FolderKanban,
    crm: Users
};

export const ShellLayout = () => {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const [apps, setApps] = useState([]);
    const [isReordering, setIsReordering] = useState(false);
    const dragItem = useRef(null);
    const dragOverItem = useRef(null);
    const location = useLocation();
    const navigate = useNavigate();
    const { user, logout, isAdmin } = useAuth();

    useEffect(() => {
        fetchApps();
        const saved = localStorage.getItem('sidebar-collapsed');
        if (saved !== null) {
            setSidebarCollapsed(saved === 'true');
        }
    }, []);

    const fetchApps = async () => {
        try {
            const response = await axios.get(`${API_URL}/apps`);
            const fetchedApps = response.data.apps;
            const savedOrder = localStorage.getItem('sidebar-app-order');
            if (savedOrder) {
                try {
                    const order = JSON.parse(savedOrder);
                    const sorted = [...fetchedApps].sort((a, b) => {
                        const ai = order.indexOf(a.id);
                        const bi = order.indexOf(b.id);
                        if (ai === -1 && bi === -1) return 0;
                        if (ai === -1) return 1;
                        if (bi === -1) return -1;
                        return ai - bi;
                    });
                    setApps(sorted);
                } catch {
                    setApps(fetchedApps);
                }
            } else {
                setApps(fetchedApps);
            }
        } catch (err) {
            console.error('Failed to fetch apps for nav:', err);
        }
    };

    const handleDragStart = (index) => {
        dragItem.current = index;
    };

    const handleDragEnter = (index) => {
        dragOverItem.current = index;
        if (dragItem.current === null || dragItem.current === index) return;
        const newApps = [...apps];
        const dragged = newApps.splice(dragItem.current, 1)[0];
        newApps.splice(index, 0, dragged);
        dragItem.current = index;
        setApps(newApps);
    };

    const handleDragEnd = () => {
        localStorage.setItem('sidebar-app-order', JSON.stringify(apps.map(a => a.id)));
        dragItem.current = null;
        dragOverItem.current = null;
    };

    const handleLogout = () => {
        logout();
        toast.success('Signed out successfully');
        navigate('/', { replace: true });
    };

    const toggleSidebarCollapsed = () => {
        const newState = !sidebarCollapsed;
        setSidebarCollapsed(newState);
        localStorage.setItem('sidebar-collapsed', String(newState));
    };

    const isActive = (path) => {
        return location.pathname === path || location.pathname.startsWith(path + '/');
    };

    const getCurrentApp = () => {
        const appPath = location.pathname.split('/')[2];
        return apps.find(app => app.id === appPath);
    };

    const currentApp = getCurrentApp();

    const getInitials = (name) => {
        if (!name) return 'U';
        return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    };

    const getAppIcon = (appId) => {
        return APP_ICONS[appId] || Grid3X3;
    };

    // Nav item component with tooltip support
    const NavItem = ({ to, icon: Icon, label, isActiveState, onClick }) => {
        const content = (
            <Link
                to={to}
                className={`nav-item ${isActiveState ? 'active' : ''} ${sidebarCollapsed ? 'justify-center px-0' : ''}`}
                onClick={onClick}
                data-testid={`nav-${label.toLowerCase().replace(/\s+/g, '-')}`}
            >
                <Icon className="w-5 h-5 flex-shrink-0" />
                {!sidebarCollapsed && <span>{label}</span>}
            </Link>
        );

        if (sidebarCollapsed) {
            return (
                <Tooltip delayDuration={0}>
                    <TooltipTrigger asChild>
                        {content}
                    </TooltipTrigger>
                    <TooltipContent side="right" className="font-medium">
                        {label}
                    </TooltipContent>
                </Tooltip>
            );
        }

        return content;
    };

    return (
        <TooltipProvider>
            <div className="shell-wrapper" data-testid="shell-layout">
                {/* Mobile Overlay */}
                <div 
                    className={`sidebar-overlay md:hidden ${sidebarOpen ? 'visible' : ''}`}
                    onClick={() => setSidebarOpen(false)}
                />

                {/* Sidebar */}
                <aside 
                    className={`shell-sidebar ${sidebarOpen ? 'open' : ''} ${sidebarCollapsed ? 'collapsed' : ''}`} 
                    data-testid="shell-sidebar"
                    style={{ width: sidebarCollapsed ? '72px' : '256px' }}
                >
                    {/* Logo */}
                    <div className={`p-5 border-b border-slate-100 ${sidebarCollapsed ? 'px-3' : ''}`}>
                        <Link to="/dashboard" className="flex items-center gap-2">
                            <div className="w-9 h-9 bg-indigo-600 rounded-lg flex items-center justify-center flex-shrink-0">
                                <Grid3X3 className="w-5 h-5 text-white" />
                            </div>
                            {!sidebarCollapsed && (
                                <span className="text-xl font-bold text-slate-900 tracking-tight">AppShell</span>
                            )}
                        </Link>
                    </div>

                    {/* Navigation */}
                    <nav className={`flex-1 p-4 overflow-y-auto ${sidebarCollapsed ? 'px-2' : ''}`}>
                        {/* Main Navigation */}
                        <div className="mb-6">
                            {!sidebarCollapsed && (
                                <p className="px-3 mb-2 text-xs font-semibold uppercase tracking-wider text-slate-400">
                                    Platform
                                </p>
                            )}
                            <NavItem
                                to="/dashboard"
                                icon={Home}
                                label="Dashboard"
                                isActiveState={isActive('/dashboard') && !currentApp}
                                onClick={() => setSidebarOpen(false)}
                            />
                            {isAdmin && (
                                <NavItem
                                    to="/user-management"
                                    icon={UserCog}
                                    label="User Management"
                                    isActiveState={isActive('/user-management')}
                                    onClick={() => setSidebarOpen(false)}
                                />
                            )}
                        </div>

                        {/* Apps Navigation */}
                        <div>
                            {!sidebarCollapsed && (
                                <div className="flex items-center justify-between px-3 mb-2">
                                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                                        Applications
                                    </p>
                                    <button
                                        onClick={() => setIsReordering(r => !r)}
                                        className="text-slate-400 hover:text-slate-600 transition-colors"
                                        title={isReordering ? 'Done reordering' : 'Reorder apps'}
                                    >
                                        {isReordering ? (
                                            <Check className="w-3.5 h-3.5" />
                                        ) : (
                                            <Pencil className="w-3.5 h-3.5" />
                                        )}
                                    </button>
                                </div>
                            )}
                            {apps.map((app, index) => {
                                const Icon = getAppIcon(app.id);
                                if (isReordering) {
                                    return (
                                        <div
                                            key={app.id}
                                            draggable
                                            onDragStart={() => handleDragStart(index)}
                                            onDragEnter={() => handleDragEnter(index)}
                                            onDragEnd={handleDragEnd}
                                            onDragOver={e => e.preventDefault()}
                                            className="flex items-center gap-2 px-3 py-2 rounded-lg cursor-grab active:cursor-grabbing hover:bg-slate-100 transition-colors select-none"
                                        >
                                            <GripVertical className="w-4 h-4 text-slate-400 flex-shrink-0" />
                                            <Icon className="w-4 h-4 text-slate-500 flex-shrink-0" />
                                            <span className="text-sm text-slate-700 truncate">{app.name}</span>
                                        </div>
                                    );
                                }
                                return (
                                    <NavItem
                                        key={app.id}
                                        to={app.path}
                                        icon={Icon}
                                        label={app.name}
                                        isActiveState={isActive(app.path)}
                                        onClick={() => setSidebarOpen(false)}
                                    />
                                );
                            })}
                        </div>
                    </nav>

                    {/* Collapse Toggle Button */}
                    <div className={`px-4 py-2 border-t border-slate-100 ${sidebarCollapsed ? 'px-2' : ''}`}>
                        <button
                            onClick={toggleSidebarCollapsed}
                            className={`w-full flex items-center gap-2 p-2 rounded-lg hover:bg-slate-100 transition-colors text-slate-500 hover:text-slate-700 ${sidebarCollapsed ? 'justify-center' : ''}`}
                            data-testid="toggle-sidebar-btn"
                        >
                            {sidebarCollapsed ? (
                                <ChevronRight className="w-5 h-5" />
                            ) : (
                                <>
                                    <ChevronLeft className="w-5 h-5" />
                                    <span className="text-sm">Collapse</span>
                                </>
                            )}
                        </button>
                    </div>

                    {/* User Section */}
                    <div className={`p-4 border-t border-slate-100 ${sidebarCollapsed ? 'px-2' : ''}`}>
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                {sidebarCollapsed ? (
                                    <Tooltip delayDuration={0}>
                                        <TooltipTrigger asChild>
                                            <button 
                                                className="w-full flex items-center justify-center p-2 rounded-lg hover:bg-slate-50 transition-colors"
                                                data-testid="user-menu-trigger"
                                            >
                                                <Avatar className="h-9 w-9">
                                                    <AvatarFallback className="bg-indigo-100 text-indigo-600 font-semibold">
                                                        {getInitials(user?.name)}
                                                    </AvatarFallback>
                                                </Avatar>
                                            </button>
                                        </TooltipTrigger>
                                        <TooltipContent side="right">
                                            {user?.name || 'User'}
                                        </TooltipContent>
                                    </Tooltip>
                                ) : (
                                    <button 
                                        className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 transition-colors"
                                        data-testid="user-menu-trigger"
                                    >
                                        <Avatar className="h-9 w-9">
                                            <AvatarFallback className="bg-indigo-100 text-indigo-600 font-semibold">
                                                {getInitials(user?.name)}
                                            </AvatarFallback>
                                        </Avatar>
                                        <div className="flex-1 text-left">
                                            <p className="text-sm font-medium text-slate-900 truncate">
                                                {user?.name || 'User'}
                                            </p>
                                            <p className="text-xs text-slate-500 truncate">
                                                {user?.email || 'user@example.com'}
                                            </p>
                                        </div>
                                    </button>
                                )}
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-56">
                                <DropdownMenuItem className="cursor-pointer" data-testid="user-settings-btn">
                                    <Settings className="w-4 h-4 mr-2" />
                                    Settings
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem 
                                    className="cursor-pointer text-red-600 focus:text-red-600"
                                    onClick={handleLogout}
                                    data-testid="user-logout-btn"
                                >
                                    <LogOut className="w-4 h-4 mr-2" />
                                    Sign Out
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                </aside>

                {/* Main Content */}
                <main 
                    className="shell-main"
                    style={{ marginLeft: sidebarCollapsed ? '72px' : '256px' }}
                >
                    {/* Mobile Header - Only shown on mobile */}
                    <header className="sticky top-0 z-30 backdrop-blur-md bg-white/80 border-b border-slate-100 md:hidden">
                        <div className="flex items-center justify-between px-4 h-14">
                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => setSidebarOpen(!sidebarOpen)}
                                data-testid="mobile-menu-btn"
                            >
                                {sidebarOpen ? (
                                    <X className="w-5 h-5" />
                                ) : (
                                    <Menu className="w-5 h-5" />
                                )}
                            </Button>
                            <span className="text-lg font-semibold text-slate-900">AppShell</span>
                            <div className="w-10" /> {/* Spacer for centering */}
                        </div>
                    </header>

                    {/* Page Content */}
                    <div className="min-h-screen">
                        <Outlet />
                    </div>
                </main>
            </div>
        </TooltipProvider>
    );
};

export default ShellLayout;
